import type {
  AccountPermission,
  AllPermissions,
  OrganizationPermission,
  ResourceType,
  SitePermission,
} from './types';
import { useAuthorizationWithUserId } from './useAuthorization';
import { accountPermissions, organizationPermissions, sitePermissions } from './permissions';

/**
 * usePermission hook is a utility function that unifies all permission checks.
 * It is an extension of the useAuthorization() hook.
 * It detects the resource type according to permission,
 * and it gets the id from useCurrentXYZ() hook.
 *
 * If the provided permission doesn't fit any resource type, the hook returns false.
 *
 * If you need a more customizable solution, please check the useAuthorization().
 *
 * @param permission
 * @param userId
 * @param currentSiteAccountId
 * @param currentOrganizationId
 * @param currentSiteId
 */
// eslint-disable-next-line func-params-args/func-params
export const usePermissionWithDependencies = (
  permission: AllPermissions,
  userId?: number,
  currentSiteAccountId?: number,
  currentOrganizationId?: number,
  currentSiteId?: string,
): boolean => {
  const { hasPermission } = useAuthorizationWithUserId(userId);

  let resourceType: ResourceType | undefined;
  let resourceId;

  if (accountPermissions.includes(permission as AccountPermission)) {
    resourceType = 'account';
    resourceId = currentSiteAccountId;
  } else if (organizationPermissions.includes(permission as OrganizationPermission)) {
    resourceType = 'organization';
    resourceId = currentOrganizationId;
  } else if (sitePermissions.includes(permission as SitePermission)) {
    resourceType = 'site';
    resourceId = currentSiteId;
  }

  if (resourceType && resourceId) {
    return hasPermission(resourceType, `${resourceId}`, permission);
  }

  return false;
};
